import { Controller } from "@hotwired/stimulus"
import type { FrameElement, TurboSubmitEndEvent } from "@hotwired/turbo"
import camelCase from "lodash/camelCase"

const ALLOWED_PROPERTIES: ReadonlySet<string> = new Set([
  "value",
  "textContent",
  "action",
])

export default class ModalController extends Controller<HTMLElement> {
  static targets = ["title", "frame", "populate", "errors"]

  declare titleTarget: HTMLElement
  declare hasTitleTarget: boolean
  declare frameTarget: FrameElement
  declare hasFrameTarget: boolean
  declare populateTargets: HTMLElement[]
  declare errorsTargets: HTMLElement[]

  connect(): void {
    this.element.addEventListener("hidden.bs.modal", this.resetForms)
  }

  disconnect(): void {
    this.element.removeEventListener("hidden.bs.modal", this.resetForms)
  }

  closeOnSuccess(event: TurboSubmitEndEvent): void {
    if (!event.detail.success) return

    const redirected = event.detail.fetchResponse?.response?.redirected
    if (redirected) return

    this.hide()
  }

  hide(): void {
    const modal = globalThis.bootstrap.Modal.getInstance(this.element)
    modal?.hide()
  }

  // Populates modal elements from trigger button data-bs-* attributes.
  // Convention: data-populate-{property}="{bs-key}" sets el[property] from data-bs-{bs-key}.
  populate(event: Event): void {
    const button = (event as Event & { relatedTarget: HTMLElement }).relatedTarget
    if (!button) return

    this.errorsTargets.forEach((el) => (el.innerHTML = ""))

    if (this.hasTitleTarget) {
      const title = button.dataset.bsTitle
      if (title) this.titleTarget.textContent = title
    }

    if (this.hasFrameTarget) {
      const src = button.dataset.bsFrameSrc
      if (src) this.frameTarget.src = src
    }

    this.populateTargets.forEach((el) => {
      this.populateBindings(el).forEach(({ property, bsKey }) => {
        const value = button.dataset[camelCase(`bs-${bsKey}`)]
        if (value != null) {
          ;(el as unknown as Record<string, string>)[property] = value
        }
      })
    })
  }

  private resetForms = (): void => {
    this.element.querySelectorAll("form").forEach((form) => form.reset())
  }

  private populateBindings(
    el: HTMLElement,
  ): { property: string; bsKey: string }[] {
    return Object.entries(el.dataset).flatMap(([dataKey, bsKey]) => {
      if (!dataKey.startsWith("populate") || dataKey === "populate" || !bsKey)
        return []
      const property = dataKey.charAt(8).toLowerCase() + dataKey.slice(9)
      return ALLOWED_PROPERTIES.has(property) ? [{ property, bsKey }] : []
    })
  }
}
